import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  FileText, Plus, Search, Trash2, Loader2, Pencil, Upload, FileType, Sparkles,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const TYPE_LABEL = { pdf: "PDF", txt: "TXT", doc: "DOC", docx: "DOCX" };

export default function DocumentsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";

  const [docs, setDocs] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [q, setQ] = useState("");
  const [sectorFilter, setSectorFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "", sector_id: "", file: null });
  const [uploading, setUploading] = useState(false);

  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [suggestedFrom, setSuggestedFrom] = useState("");

  const fileRef = useRef();
  const [searchParams, setSearchParams] = useSearchParams();

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (isAdmin && sectorFilter !== "all") params.sector_id = sectorFilter;
      const [d, s] = await Promise.all([
        api.get("/documents", { params }),
        isAdmin ? api.get("/sectors") : Promise.resolve({ data: [] }),
      ]);
      setDocs(d.data);
      if (isAdmin) setSectors(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, sectorFilter]);

  // Handle "Sugerir documento" from Analytics: ?suggest=<question>&sector_id=<id>
  useEffect(() => {
    const suggest = searchParams.get("suggest");
    const sid = searchParams.get("sector_id");
    if (suggest) {
      const truncated = suggest.length > 80 ? suggest.slice(0, 80).trim() + "…" : suggest;
      setForm({
        name: truncated,
        type: "",
        sector_id: isAdmin ? (sid || "") : (user?.sector_id || ""),
        file: null,
      });
      setSuggestedFrom(suggest);
      setOpen(true);
      // Clear the query params so it doesn't re-open on refresh
      const next = new URLSearchParams(searchParams);
      next.delete("suggest");
      next.delete("sector_id");
      setSearchParams(next, { replace: true });
      setTimeout(() => fileRef.current && (fileRef.current.value = ""), 50);
    }
    // eslint-disable-next-line
  }, [searchParams, user]);

  function openUpload() {
    setForm({ name: "", type: "", sector_id: isAdmin ? "" : (user?.sector_id || ""), file: null });
    setSuggestedFrom("");
    setOpen(true);
    setTimeout(() => fileRef.current && (fileRef.current.value = ""), 50);
  }

  async function submitUpload() {
    if (!form.file) return toast.error("Selecione um arquivo");
    if (!form.name.trim()) return toast.error("Informe o nome");
    if (!form.type) return toast.error("Selecione o tipo");
    if (isAdmin && !form.sector_id) return toast.error("Selecione o setor");

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("type", form.type);
      if (isAdmin) fd.append("sector_id", form.sector_id);
      fd.append("file", form.file);
      const { data } = await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.warning) toast.warning(data.warning);
      else toast.success(`Documento processado (${data.chunk_count} chunks)`);
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no upload");
    } finally { setUploading(false); }
  }

  async function confirmDelete() {
    try {
      await api.delete(`/documents/${deleteId}`);
      toast.success("Excluído");
      setDeleteId(null);
      await load();
    } catch (e) { toast.error("Erro ao excluir"); }
  }

  async function commitRename() {
    try {
      await api.put(`/documents/${renameId}`, { name: renameValue });
      setRenameId(null);
      await load();
      toast.success("Renomeado");
    } catch (e) { toast.error("Erro"); }
  }

  const detectTypeFromFile = (file) => {
    if (!file) return "";
    const ext = file.name.split(".").pop().toLowerCase();
    return ["pdf", "txt", "doc", "docx"].includes(ext) ? ext : "";
  };

  return (
    <div>
      <PageHeader
        title="Documentos"
        subtitle={isAdmin ? "Base de conhecimento de todos os setores." : "Base de conhecimento do seu setor."}
        action={
          <Button data-testid="new-doc-btn" onClick={openUpload} className="bg-[#1e5a8e] hover:bg-[#164570]">
            <Plus className="w-4 h-4 mr-2" /> Novo documento
          </Button>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="doc-search"
            placeholder="Buscar documentos..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        {isAdmin && (
          <Select value={sectorFilter} onValueChange={setSectorFilter}>
            <SelectTrigger className="w-full sm:w-64" data-testid="sector-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os setores</SelectItem>
              {sectors.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : docs.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="Nenhum documento"
          description="Envie PDFs, TXTs ou DOCXs para treinar o chatbot com o conhecimento do seu setor."
          action={<Button onClick={openUpload} className="bg-[#1e5a8e] hover:bg-[#164570]"><Upload className="w-4 h-4 mr-2" />Enviar primeiro documento</Button>}
        />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Nome</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tipo</th>
                {isAdmin && <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Setor</th>}
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Chunks</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Enviado em</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium flex items-center gap-2">
                    <FileType className="w-4 h-4 text-slate-400" /> {d.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600"><Badge variant="outline">{TYPE_LABEL[d.type] || d.type}</Badge></td>
                  {isAdmin && <td className="px-6 py-4 text-sm text-slate-600">{d.sector_name || "—"}</td>}
                  <td className="px-6 py-4 text-sm text-slate-600">{d.chunk_count}</td>
                  <td className="px-6 py-4 text-sm text-slate-500">{new Date(d.created_at).toLocaleDateString("pt-BR")}</td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => { setRenameId(d.id); setRenameValue(d.name); }} data-testid={`rename-doc-${d.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setDeleteId(d.id)} data-testid={`delete-doc-${d.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Upload dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSuggestedFrom(""); }}>
        <DialogContent data-testid="doc-upload-dialog">
          <DialogHeader>
            <DialogTitle>Novo documento</DialogTitle>
            {suggestedFrom && (
              <DialogDescription className="pt-1">
                <span className="inline-flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 text-xs leading-relaxed">
                  <Sparkles className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                  <span data-testid="suggested-from-hint">
                    Sugerido a partir de uma pergunta sem resposta:
                    <br />
                    <em>"{suggestedFrom}"</em>
                  </span>
                </span>
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nome do documento *</Label>
              <Input data-testid="doc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Manual do Aluno 2026" />
            </div>
            <div>
              <Label>Arquivo *</Label>
              <Input
                ref={fileRef}
                data-testid="doc-file"
                type="file"
                accept=".pdf,.txt,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  const type = detectTypeFromFile(file);
                  setForm((f) => ({ ...f, file, type, name: f.name || (file?.name?.replace(/\.[^.]+$/, "") || "") }));
                }}
              />
            </div>
            <div>
              <Label>Tipo *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="doc-type"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="txt">TXT</SelectItem>
                  <SelectItem value="docx">DOCX</SelectItem>
                  <SelectItem value="doc">DOC</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div>
                <Label>Setor *</Label>
                <Select value={form.sector_id} onValueChange={(v) => setForm({ ...form, sector_id: v })}>
                  <SelectTrigger data-testid="doc-sector"><SelectValue placeholder="Selecione o setor" /></SelectTrigger>
                  <SelectContent>
                    {sectors.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={uploading}>Cancelar</Button>
            <Button data-testid="submit-upload-btn" onClick={submitUpload} disabled={uploading} className="bg-[#1e5a8e] hover:bg-[#164570]">
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processando…</> : <><Upload className="w-4 h-4 mr-2" /> Enviar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renameId} onOpenChange={(o) => !o && setRenameId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Renomear documento</DialogTitle></DialogHeader>
          <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameId(null)}>Cancelar</Button>
            <Button onClick={commitRename} className="bg-[#1e5a8e] hover:bg-[#164570]">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir documento?</AlertDialogTitle>
            <AlertDialogDescription>Todos os chunks vetoriais serão removidos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
